import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getMultiYearCurriculums,
  createMultiYearCurriculum,
  updateMultiYearCurriculum,
  deleteMultiYearCurriculum,
  getDegreesAuthorized,
  getAdmissionTestsAuthorized
} from '../../../API/API';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { HiOutlineX, HiChevronDown, HiChevronRight, HiOutlinePencil, HiOutlineTrash, HiOutlinePlus, HiOutlineDownload } from 'react-icons/hi';
import Spinner from '../../../components/Spinner';
import Container from '../../../components/UI/Container';

function MultiSelectFilter({ label, options, selected, onChange, hasNotSet = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const count = selected.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition ${
          count > 0 ? 'border-primary bg-primary/5 text-primary' : 'border-gray-300 text-gray-700 hover:border-gray-400'
        }`}
      >
        <span className="truncate max-w-[140px]">{label}</span>
        {count > 0 && (
          <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-white">
            {count}
          </span>
        )}
        <HiChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] max-h-[280px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {hasNotSet && (
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100">
              <input
                type="checkbox"
                checked={selected.includes('null')}
                onChange={() => toggle('null')}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="italic text-gray-500">Not Set</span>
            </label>
          )}
          {options.map(opt => (
            <label key={opt.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50">
              <input
                type="checkbox"
                checked={selected.includes(String(opt.id))}
                onChange={() => toggle(String(opt.id))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="truncate">{opt.name}</span>
            </label>
          ))}
          {count > 0 && (
            <div className="sticky bottom-0 border-t border-gray-100 bg-white px-3 py-1.5">
              <button
                type="button"
                onClick={() => { onChange([]); setOpen(false); }}
                className="text-xs text-gray-500 hover:text-primary transition"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const universityOptions = [
  { id: 1, name: 'Oxford' },
  { id: 2, name: 'Cambridge' },
  { id: 3, name: 'NonOxbridge' },
  { id: 4, name: 'Imperial' },
  { id: 5, name: 'UCL'}
];

const programModuleOptions = [
  { id: 1, name: 'Admission Test' },
  { id: 2, name: 'Interview' },
  { id: 3, name: 'Personal Statement' },
  { id: 4, name: 'All' }
];

const componentTypeOptions = [
  { id: 1, name: 'Academy Videos' },
  { id: 2, name: 'Book Pages' },
  { id: 3, name: 'Course' },
  { id: 4, name: 'Revise Questions' },
  { id: 5, name: 'Simulate Exams' },
  { id: 6, name: 'Learn Module' },
  { id: 7, name: 'Seminar' },
  { id: 8, name: 'Essay Submission' },
  { id: 9, name: 'Personal Statement Submission' },
  { id: 10, name: 'Tutoring' },
  //{ id: 11, name: 'Other' },
  //{ id: 12, name: 'Enrollment Month Only' },
  //{ id: 13, name: 'HeadStart Article' },
  //{ id: 14, name: 'HeadStart Book' },
  //{ id: 15, name: 'Extended Course' }
];

const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getUniversityName(val) {
  return universityOptions.find(u => u.id === val)?.name || '-';
}
function getProgramName(val) {
  return programModuleOptions.find(p => p.id === val)?.name || '-';
}
function getComponentName(val) {
  return componentTypeOptions.find(c => c.id === val)?.name || '-';
}
function formatPeriod(startYear, startMonth, endYear, endMonth) {
  if (!startYear && !startMonth && !endYear && !endMonth) return '-';
  const s = `${monthNames[startMonth] || '?'}/${startYear || '?'}`;
  const e = `${monthNames[endMonth] || '?'}/${endYear || '?'}`;
  return `${s} - ${e}`;
}

const emptySplit = () => ({ startYear: '', startMonth: '', endYear: '', endMonth: '', percentage: '' });

const emptyForm = () => ({
  name: '',
  degreeId: '',
  universityType: '',
  admissionTestId: '',
  programModule: 4,
  curriculumCompnentType: 1,
  number: '',
  startYear: '',
  startMonth: '',
  endYear: '',
  endMonth: '',
  courseDate: '',
  offset: 9,
  itemId: '',
  splits: []
});

function MultiYearCurriculumAdmin() {
  const [data, setData] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [admissionTests, setAdmissionTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterUniversity, setFilterUniversity] = useState([]);
  const [filterDegree, setFilterDegree] = useState([]);
  const [filterComponent, setFilterComponent] = useState([]);
  const [filterAdmissionTest, setFilterAdmissionTest] = useState([]);
  const [inclusiveMode, setInclusiveMode] = useState(false);

  // Column toggles
  const [showCourseDate, setShowCourseDate] = useState(false);
  const [showOffset, setShowOffset] = useState(false);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState({});

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const debouncedSearchHandler = useCallback(
    _.debounce((val) => setDebouncedSearch(val), 400),
    []
  );

  useEffect(() => {
    debouncedSearchHandler(searchQuery);
  }, [searchQuery, debouncedSearchHandler]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [currResponse, degResponse, atResponse] = await Promise.all([
        getMultiYearCurriculums(),
        getDegreesAuthorized(),
        getAdmissionTestsAuthorized()
      ]);
      setData(currResponse.data.data);
      setDegrees(degResponse.data.data);
      setAdmissionTests(atResponse.data.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    let result = data;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(item => item.name?.toLowerCase().includes(q));
    }
    if (filterUniversity.length > 0) {
      const hasNull = filterUniversity.includes('null');
      const numericVals = filterUniversity.filter(v => v !== 'null').map(Number);
      result = result.filter(item => {
        if (hasNull && item.universityType == null) return true;
        if (numericVals.length > 0 && numericVals.includes(item.universityType)) return true;
        if (inclusiveMode && numericVals.length > 0 && item.universityType == null) return true;
        return false;
      });
    }
    if (filterDegree.length > 0) {
      const hasNull = filterDegree.includes('null');
      const idVals = filterDegree.filter(v => v !== 'null');
      result = result.filter(item => {
        if (hasNull && item.degreeId == null) return true;
        if (idVals.length > 0 && idVals.includes(item.degreeId)) return true;
        if (inclusiveMode && idVals.length > 0 && item.degreeId == null) return true;
        return false;
      });
    }
    if (filterComponent.length > 0) {
      const numericVals = filterComponent.map(Number);
      result = result.filter(item => numericVals.includes(item.curriculumCompnentType));
    }
    if (filterAdmissionTest.length > 0) {
      const hasNull = filterAdmissionTest.includes('null');
      const idVals = filterAdmissionTest.filter(v => v !== 'null');
      result = result.filter(item => {
        if (hasNull && item.admissionTestId == null) return true;
        if (idVals.length > 0 && idVals.includes(item.admissionTestId)) return true;
        if (inclusiveMode && idVals.length > 0 && item.admissionTestId == null) return true;
        return false;
      });
    }
    return result;
  }, [data, debouncedSearch, filterUniversity, filterDegree, filterComponent, filterAdmissionTest, inclusiveMode]);

  const getDegreeName = (degreeId) => {
    if (!degreeId) return '-';
    return degrees.find(d => d.id === degreeId)?.name || '-';
  };

  const getAdmissionTestName = (admissionTestId) => {
    if (!admissionTestId) return '-';
    return admissionTests.find(a => a.id === admissionTestId)?.name || '-';
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      degreeId: item.degreeId || '',
      universityType: item.universityType ?? '',
      admissionTestId: item.admissionTestId || '',
      programModule: item.programModule ?? 4,
      curriculumCompnentType: item.curriculumCompnentType ?? 1,
      number: item.number ?? '',
      startYear: item.startYear ?? '',
      startMonth: item.startMonth ?? '',
      endYear: item.endYear ?? '',
      endMonth: item.endMonth ?? '',
      courseDate: item.courseDate ? item.courseDate.substring(0, 10) : '',
      offset: item.offset ?? 0,
      itemId: item.itemId || '',
      splits: item.multiYearCurriculumSplits?.map(s => ({
        startYear: s.startYear,
        startMonth: s.startMonth,
        endYear: s.endYear,
        endMonth: s.endMonth,
        percentage: s.percentage
      })) || []
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setForm(emptyForm());
  };

  const updateFormField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addSplit = () => {
    setForm(prev => ({ ...prev, splits: [...prev.splits, emptySplit()] }));
  };

  const removeSplit = (index) => {
    setForm(prev => ({ ...prev, splits: prev.splits.filter((_, i) => i !== index) }));
  };

  const updateSplit = (index, field, value) => {
    setForm(prev => {
      const newSplits = [...prev.splits];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { ...prev, splits: newSplits };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const totalPercentage = form.splits.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0);
    if (form.splits.length > 0 && totalPercentage !== 100) {
      toast.error(`Split percentages must total 100% (currently ${totalPercentage}%)`);
      return;
    }

    const payload = {
      name: form.name.trim(),
      degreeId: form.degreeId || null,
      universityType: form.universityType ? Number(form.universityType) : null,
      admissionTestId: form.admissionTestId || null,
      programModule: Number(form.programModule),
      curriculumCompnentType: Number(form.curriculumCompnentType),
      number: form.number !== '' ? Number(form.number) : null,
      startYear: form.startYear !== '' ? Number(form.startYear) : null,
      startMonth: form.startMonth !== '' ? Number(form.startMonth) : null,
      endYear: form.endYear !== '' ? Number(form.endYear) : null,
      endMonth: form.endMonth !== '' ? Number(form.endMonth) : null,
      courseDate: form.courseDate || null,
      offset: Number(form.offset) || 0,
      itemId: form.itemId || null,
      splits: form.splits.map(s => ({
        startYear: Number(s.startYear),
        startMonth: Number(s.startMonth),
        endYear: Number(s.endYear),
        endMonth: Number(s.endMonth),
        percentage: Number(s.percentage)
      }))
    };

    try {
      setSaving(true);
      if (editingItem) {
        payload.id = editingItem.id;
        await updateMultiYearCurriculum(payload);
        toast.success('Updated successfully');
      } else {
        await createMultiYearCurriculum(payload);
        toast.success('Created successfully');
      }
      closeModal();
      await fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setSaving(true);
      await deleteMultiYearCurriculum(id);
      toast.success('Deleted successfully');
      setDeleteConfirm(null);
      await fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const activeFilterCount = [filterUniversity, filterDegree, filterComponent, filterAdmissionTest].filter(a => a.length > 0).length;

  const clearAllFilters = () => {
    setFilterUniversity([]);
    setFilterDegree([]);
    setFilterComponent([]);
    setFilterAdmissionTest([]);
    setSearchQuery('');
  };

  const exportCsv = useCallback(() => {
    const escapeCsv = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const headers = [
      'Name', 'University', 'Degree', 'Admission Test', 'Component', 'Program',
      'Number', 'Start Month', 'Start Year', 'End Month', 'End Year',
      'Course Date', 'Item ID', 'Splits Count',
      'Split #', 'Split Start Month', 'Split Start Year', 'Split End Month', 'Split End Year', 'Split %'
    ];

    const rows = [];
    filteredData.forEach(item => {
      const splits = item.multiYearCurriculumSplits || [];
      const base = [
        item.name,
        getUniversityName(item.universityType),
        getDegreeName(item.degreeId),
        getAdmissionTestName(item.admissionTestId),
        getComponentName(item.curriculumCompnentType),
        getProgramName(item.programModule),
        item.number ?? '',
        monthNames[item.startMonth] || '',
        item.startYear || '',
        monthNames[item.endMonth] || '',
        item.endYear || '',
        item.courseDate ? new Date(item.courseDate).toLocaleDateString('en-GB') : '',
        item.itemId || '',
        splits.length
      ];

      if (splits.length === 0) {
        rows.push([...base, '', '', '', '', '', ''].map(escapeCsv).join(','));
      } else {
        splits.forEach((s, idx) => {
          rows.push([...base, idx + 1, monthNames[s.startMonth] || '', s.startYear || '', monthNames[s.endMonth] || '', s.endYear || '', s.percentage ?? ''].map(escapeCsv).join(','));
        });
      }
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `multi-year-curriculum-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredData, degrees, admissionTests]);

  if (loading) return <Spinner type="child" />;
  if (error) return <Container className="container-in p-8 text-red-500">{error}</Container>;

  return (
    <Container className="container-in pb-10">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-800">Multi-Year Curriculum Admin</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
            title="Export current view as CSV"
          >
            <HiOutlineDownload size={18} />
            Export ({filteredData.length})
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-white hover:opacity-90 transition"
          >
            <HiOutlinePlus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-w-[220px] rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
          />
          <MultiSelectFilter
            label="University"
            options={universityOptions}
            selected={filterUniversity}
            onChange={setFilterUniversity}
            hasNotSet
          />
          <MultiSelectFilter
            label="Degree"
            options={degrees}
            selected={filterDegree}
            onChange={setFilterDegree}
            hasNotSet
          />
          <MultiSelectFilter
            label="Component"
            options={componentTypeOptions}
            selected={filterComponent}
            onChange={setFilterComponent}
          />
          <MultiSelectFilter
            label="Adm. Test"
            options={admissionTests}
            selected={filterAdmissionTest}
            onChange={setFilterAdmissionTest}
            hasNotSet
          />
          <button
            onClick={() => setInclusiveMode(prev => !prev)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition ${
              inclusiveMode
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="When on, filters also include records where the field is not set"
          >
            <span className={`inline-block h-3 w-3 rounded-sm border ${inclusiveMode ? 'bg-white border-white' : 'border-gray-400'}`}>
              {inclusiveMode && <span className="block h-full w-full text-primary text-[10px] leading-3 text-center font-bold">&#10003;</span>}
            </span>
            Include unset
          </button>
          <button
            onClick={() => setShowCourseDate(prev => !prev)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition ${
              showCourseDate
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Toggle course date column visibility"
          >
            <span className={`inline-block h-3 w-3 rounded-sm border ${showCourseDate ? 'bg-white border-white' : 'border-gray-400'}`}>
              {showCourseDate && <span className="block h-full w-full text-primary text-[10px] leading-3 text-center font-bold">&#10003;</span>}
            </span>
            Course Date
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 hover:bg-gray-200 transition"
            >
              <HiOutlineX size={14} />
              Clear filters ({activeFilterCount})
            </button>
          )}
          <span className="ml-auto text-sm text-gray-500">
            {filteredData.length} item{filteredData.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
              <th className="w-8 px-3 py-3"></th>
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">University</th>
              <th className="px-3 py-3">Degree</th>
              <th className="px-3 py-3">Adm. Test</th>
              <th className="px-3 py-3">Component</th>
              <th className="px-3 py-3">Program</th>
              <th className="px-3 py-3 text-center">Num</th>
              {showOffset && <th className="px-3 py-3 text-center">Offset</th>}
              <th className="px-3 py-3">Period</th>
              {showCourseDate && <th className="px-3 py-3">Course Date</th>}
              <th className="px-3 py-3 text-center">Splits</th>
              <th className="px-3 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 && (
              <tr>
                <td colSpan={showCourseDate ? 13 : 12} className="px-6 py-12 text-center text-gray-400">
                  No items found. {activeFilterCount > 0 ? 'Try adjusting your filters.' : 'Click "Add New" to create one.'}
                </td>
              </tr>
            )}
            {filteredData.map(item => {
              const isExpanded = expandedRows[item.id];
              const splits = item.multiYearCurriculumSplits || [];
              return (
                <RowGroup
                  key={item.id}
                  item={item}
                  splits={splits}
                  isExpanded={isExpanded}
                  toggleRow={toggleRow}
                  getDegreeName={getDegreeName}
                  getAdmissionTestName={getAdmissionTestName}
                  showCourseDate={showCourseDate}
                  showOffset={showOffset}
                  onEdit={openEditModal}
                  onDelete={setDeleteConfirm}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          title={editingItem ? 'Edit Multi-Year Curriculum' : 'Create Multi-Year Curriculum'}
          onClose={closeModal}
        >
          <CurriculumForm
            form={form}
            degrees={degrees}
            admissionTests={admissionTests}
            updateFormField={updateFormField}
            addSplit={addSplit}
            removeSplit={removeSplit}
            updateSplit={updateSplit}
            onSave={handleSave}
            onCancel={closeModal}
            saving={saving}
            isEdit={!!editingItem}
          />
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Modal title="Confirm Delete" onClose={() => setDeleteConfirm(null)} small>
          <div className="p-1">
            <p className="mb-2 text-gray-700">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
            </p>
            {(deleteConfirm.multiYearCurriculumSplits?.length || 0) > 0 && (
              <p className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-700">
                This item has {deleteConfirm.multiYearCurriculumSplits.length} associated split(s). 
                The splits will remain but the parent curriculum will be marked as deleted.
              </p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={saving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 transition disabled:opacity-50"
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Container>
  );
}

function RowGroup({ item, splits, isExpanded, toggleRow, getDegreeName, getAdmissionTestName, showCourseDate, showOffset, onEdit, onDelete }) {
  const hasSplits = splits.length > 0;
  return (
    <>
      <tr className={`border-b hover:bg-gray-50 transition ${isExpanded ? 'bg-blue-50/30' : ''}`}>
        <td className="px-3 py-3">
          {hasSplits && (
            <button onClick={() => toggleRow(item.id)} className="text-gray-400 hover:text-gray-700 transition">
              {isExpanded ? <HiChevronDown size={18} /> : <HiChevronRight size={18} />}
            </button>
          )}
        </td>
        <td className="px-3 py-3 font-medium text-gray-800 max-w-[200px] truncate" title={item.name}>{item.name}</td>
        <td className="px-3 py-3 text-gray-600">{getUniversityName(item.universityType)}</td>
        <td className="px-3 py-3 text-gray-600">{getDegreeName(item.degreeId)}</td>
        <td className="px-3 py-3 text-gray-600">{getAdmissionTestName(item.admissionTestId)}</td>
        <td className="px-3 py-3 text-gray-600">{getComponentName(item.curriculumCompnentType)}</td>
        <td className="px-3 py-3 text-gray-600">{getProgramName(item.programModule)}</td>
        <td className="px-3 py-3 text-center text-gray-600">{item.number ?? '-'}</td>
        {showOffset && <td className="px-3 py-3 text-center text-gray-600">{item.offset}</td>}
        <td className="px-3 py-3 text-gray-600 whitespace-nowrap text-xs">
          {formatPeriod(item.startYear, item.startMonth, item.endYear, item.endMonth)}
        </td>
        {showCourseDate && (
          <td className="px-3 py-3 text-gray-600 whitespace-nowrap text-xs">
            {item.courseDate ? new Date(item.courseDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
          </td>
        )}
        <td className="px-3 py-3 text-center">
          {hasSplits ? (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
              {splits.length}
            </span>
          ) : (
            <span className="text-xs text-gray-400">0</span>
          )}
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => onEdit(item)}
              className="rounded-md p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition"
              title="Edit"
            >
              <HiOutlinePencil size={16} />
            </button>
            <button
              onClick={() => onDelete(item)}
              className="rounded-md p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 transition"
              title="Delete"
            >
              <HiOutlineTrash size={16} />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && splits.map((split, idx) => (
        <tr key={split.id || idx} className="border-b bg-blue-50/20">
          <td className="px-3 py-2"></td>
          <td colSpan={showCourseDate ? 9 : 8} className="px-3 py-2">
            <div className="ml-4 flex items-center gap-4 text-xs text-gray-600">
              <span className="rounded bg-gray-100 px-2 py-1 font-medium text-gray-500">Split {idx + 1}</span>
              <span>
                {monthNames[split.startMonth]}/{split.startYear} - {monthNames[split.endMonth]}/{split.endYear}
              </span>
              <span className="font-semibold text-blue-600">{split.percentage}%</span>
            </div>
          </td>
          <td colSpan="3"></td>
        </tr>
      ))}
    </>
  );
}

function Modal({ title, children, onClose, small }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose}></div>
      <div className={`relative z-10 ${small ? 'max-w-md' : 'max-w-3xl'} w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl mx-4`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition">
            <HiOutlineX size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CurriculumForm({ form, degrees, admissionTests, updateFormField, addSplit, removeSplit, updateSplit, onSave, onCancel, saving, isEdit }) {
  const splitsTotal = form.splits.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0);

  return (
    <div>
      {/* Basic Fields */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <FormField label="Name *" span="full">
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateFormField('name', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="Curriculum name"
          />
        </FormField>

        <FormField label="University">
          <select
            value={form.universityType}
            onChange={(e) => updateFormField('universityType', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">None</option>
            {universityOptions.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Degree">
          <select
            value={form.degreeId}
            onChange={(e) => updateFormField('degreeId', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">None</option>
            {degrees.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Admission Test">
          <select
            value={form.admissionTestId}
            onChange={(e) => updateFormField('admissionTestId', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">None</option>
            {admissionTests.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Component Type">
          <select
            value={form.curriculumCompnentType}
            onChange={(e) => updateFormField('curriculumCompnentType', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            {componentTypeOptions.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Program Module">
          <select
            value={form.programModule}
            onChange={(e) => updateFormField('programModule', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            {programModuleOptions.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Number">
          <input
            type="number"
            value={form.number}
            onChange={(e) => updateFormField('number', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="e.g. 10"
          />
        </FormField>

        {/*<FormField label="Offset">
          <input
            type="number"
            value={form.offset}
            onChange={(e) => updateFormField('offset', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="0"
          />
        </FormField>*/}

        <FormField label="Start Year">
          <input
            type="number"
            value={form.startYear}
            onChange={(e) => updateFormField('startYear', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="e.g. 2025"
          />
        </FormField>

        <FormField label="Start Month">
          <select
            value={form.startMonth}
            onChange={(e) => updateFormField('startMonth', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">None</option>
            {monthNames.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </FormField>

        <FormField label="End Year">
          <input
            type="number"
            value={form.endYear}
            onChange={(e) => updateFormField('endYear', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="e.g. 2026"
          />
        </FormField>

        <FormField label="End Month">
          <select
            value={form.endMonth}
            onChange={(e) => updateFormField('endMonth', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">None</option>
            {monthNames.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Course Date">
          <input
            type="date"
            value={form.courseDate}
            onChange={(e) => updateFormField('courseDate', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </FormField>

        <FormField label="Item ID" span="2">
          <input
            type="text"
            value={form.itemId}
            onChange={(e) => updateFormField('itemId', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="GUID (optional)"
          />
        </FormField>
      </div>

      {/* Splits Section */}
      <div className="mt-6 border-t pt-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-700">Splits</h3>
            {form.splits.length > 0 && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${splitsTotal === 100 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                Total: {splitsTotal}%
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={addSplit}
            className="flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:border-primary hover:text-primary transition"
          >
            <HiOutlinePlus size={14} />
            Add Split
          </button>
        </div>

        {form.splits.length === 0 && (
          <p className="py-3 text-center text-xs text-gray-400">
            No splits defined. Splits allow you to distribute the curriculum across different time periods.
          </p>
        )}

        {form.splits.map((split, idx) => (
          <div key={idx} className="mb-2 flex items-center gap-2 rounded-lg border bg-gray-50 p-3">
            <span className="mr-1 text-xs font-medium text-gray-400 shrink-0">#{idx + 1}</span>
            <div className="grid flex-1 grid-cols-5 gap-2">
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">Start Year</label>
                <input
                  type="number"
                  value={split.startYear}
                  onChange={(e) => updateSplit(idx, 'startYear', e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary focus:outline-none"
                  placeholder="2025"
                />
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">Start Month</label>
                <select
                  value={split.startMonth}
                  onChange={(e) => updateSplit(idx, 'startMonth', e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary focus:outline-none"
                >
                  <option value="">-</option>
                  {monthNames.slice(1).map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">End Year</label>
                <input
                  type="number"
                  value={split.endYear}
                  onChange={(e) => updateSplit(idx, 'endYear', e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary focus:outline-none"
                  placeholder="2026"
                />
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">End Month</label>
                <select
                  value={split.endMonth}
                  onChange={(e) => updateSplit(idx, 'endMonth', e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary focus:outline-none"
                >
                  <option value="">-</option>
                  {monthNames.slice(1).map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">Percentage</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={split.percentage}
                  onChange={(e) => updateSplit(idx, 'percentage', e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary focus:outline-none"
                  placeholder="%"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeSplit(idx)}
              className="ml-1 shrink-0 rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 transition"
              title="Remove split"
            >
              <HiOutlineX size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3 border-t pt-4">
        <button
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-5 py-2 text-sm text-gray-600 hover:bg-gray-50 transition"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-lg bg-primary px-5 py-2 text-sm text-white hover:opacity-90 transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
}

function FormField({ label, children, span }) {
  const spanClass = span === 'full' ? 'md:col-span-3' : span === '2' ? 'md:col-span-2' : '';
  return (
    <div className={spanClass}>
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      {children}
    </div>
  );
}

export default MultiYearCurriculumAdmin;
